import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, IconButton } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/joy";
import {
  SchoolIcon,
  SettingsGearIcon as Settings,
  EventIcon,
  BarChartIcon,
  InventoryMenuIcon as InventoryIcon,
  PeopleAltIcon as PeopleAlt,
  PersonIcon as Person,
  CalendarMonthIcon,
  DateRangeIcon,
  ManageAccountsIcon,
  AttachMoneyIcon,
  SRental,
  SMailOutline,
  SCentre,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "../../styles/StyledMUI";
import { useBellAlerts, useBellAlertDisplay } from "../../contexts/app/bellAlertsContext";
import { usePermissions } from "../../contexts/app/permissions";
import { buildMenuGroups, GetMenuTilesCoreDefinition } from "./config/menuConfig";
import type { MenuGroup } from "./config/menuConfig";
import type { MenuTileUi } from "../../api/zekay/menu";
import MenuHeader from "./display/MenuHeader";
import MenuTilesGrid from "./display/MenuTilesGrid";
import MenuFooter from "./display/MenuFooter";
import useAppSettings from "../../contexts/app/appSettings";
import { useCompanyBrandLogoSrc } from "../../utils/useCompanyBrandLogoSrc";
import {
  colorTokens,
  companyLogoMenuBackdropSizeSx,
  ctaAccentColor,
  menuPageMainColumnSx,
  menuPageShellSx,
  menuSliderNavSx,
  withAlpha,
} from "../../styles/globalStyles";

const MenuPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { config, configReadyForMenu } = useAppSettings();
  const { alerts, isLoading } = useBellAlerts();
  const { getForPath } = useBellAlertDisplay();
  const isDesync = config?.CustomersSync === false;
  const {
    logoSrc: menuLogoSrc,
    onLogoImgError: handleMenuLogoError,
    hasCompanyLogo,
  } = useCompanyBrandLogoSrc();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const tiles = React.useMemo<MenuTileUi[]>(() => {
    const core = GetMenuTilesCoreDefinition();
    return core.map((t) => {
      const permissionOk = hasPermission(t.access);
      const desyncOk = t.showOnlyWhenDesync !== true || isDesync;
      const showWhen = t.enabled && permissionOk && desyncOk;
      const icon =
        t.path === "/customers" ? (
          <Person sx={{ color: colorTokens.accent.contrastText }} />
        ) : t.path === "/trainings" ? (
          <SchoolIcon sx={{ color: colorTokens.accent.contrastText }} />
        ) : t.path === "/inventory" ? (
          <InventoryIcon sx={{ color: colorTokens.accent.contrastText }} />
        ) : t.path === "/planning" ? (
          <DateRangeIcon sx={{ color: colorTokens.accent.contrastText }} />
        ) : t.path === "/hr" ? (
          <PeopleAlt sx={{ color: colorTokens.accent.contrastText }} />
        ) : t.path === "/events" ? (
          <EventIcon sx={{ color: colorTokens.accent.contrastText }} />
        ) : t.path === "/excursions" ? (
          <CalendarMonthIcon sx={{ color: colorTokens.accent.contrastText }} />
        ) : t.path === "/stats" ? (
          <BarChartIcon sx={{ color: colorTokens.accent.contrastText }} />
        ) : t.path === "/users" ? (
          <ManageAccountsIcon sx={{ color: colorTokens.accent.contrastText }} />
        ) : t.path === "/billing" ? (
          <AttachMoneyIcon sx={{ color: colorTokens.accent.contrastText }} />
        ) : t.path === "/rental" ? (
          <SRental iconColor={colorTokens.accent.contrastText} />
        ) : t.path === "/communication" ? (
          <SMailOutline iconColor={colorTokens.accent.contrastText} />
        ) : t.path === "/centre" ? (
          <SCentre iconColor={colorTokens.accent.contrastText} />
        ) : (
          <Settings sx={{ color: colorTokens.accent.contrastText }} />
        );

      return { ...t, icon, showWhen };
    });
  }, [hasPermission, isDesync]);

  const groups = React.useMemo<MenuGroup[]>(
    () => buildMenuGroups(tiles, isMobile),
    [tiles, isMobile],
  );

  // Default to "centre" tab on mobile
  const [activeIdx, setActiveIdx] = React.useState(0);
  const initializedRef = React.useRef(false);

  React.useEffect(() => {
    if (!initializedRef.current && groups.length > 0) {
      initializedRef.current = true;
      const centreIdx = groups.findIndex((g) => g.id === "centre");
      setActiveIdx(centreIdx >= 0 ? centreIdx : 0);
    }
  }, [groups]);

  // Clamp index when groups change
  React.useEffect(() => {
    setActiveIdx((prev) => Math.min(prev, Math.max(0, groups.length - 1)));
  }, [groups.length]);

  const canPrev = activeIdx > 0;
  const canNext = activeIdx < groups.length - 1;

  const handlePrev = () => setActiveIdx((i) => Math.max(0, i - 1));
  const handleNext = () => setActiveIdx((i) => Math.min(groups.length - 1, i + 1));

  const touchStartX = React.useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (delta < -40 && canNext) handleNext();
    else if (delta > 40 && canPrev) handlePrev();
  };

  const handleTileClick = (path: string) => {
    navigate(path);
  };

  return (
    <React.Fragment>
      {!configReadyForMenu ? (
        <Box
          role="status"
          aria-busy="true"
          aria-label="Chargement du menu"
          sx={
            {
              ...menuPageShellSx,
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
            } as SxProps
          }
        >
          <CircularProgress size="lg" color="primary" variant="soft" />
        </Box>
      ) : (
        <Box sx={menuPageShellSx}>
          <Box sx={{ flexShrink: 0, pt: { xs: 7, sm: 7 } }}>
            <MenuHeader
              LogoSrc={menuLogoSrc}
              onLogoImgError={handleMenuLogoError}
              logoSx={hasCompanyLogo ? companyLogoMenuBackdropSizeSx : undefined}
            />
          </Box>

          <Box sx={menuPageMainColumnSx}>
            <MenuTilesGrid
              Groups={groups}
              IsMobile={isMobile}
              ActiveIdx={activeIdx}
              Alerts={alerts}
              IsAlertsLoading={isLoading}
              GetForPath={getForPath}
              OnTileClick={handleTileClick}
              OnTouchStart={onTouchStart}
              OnTouchEnd={onTouchEnd}
            />
          </Box>

          {isMobile && groups.length > 1 && (
            <Box sx={menuSliderNavSx}>
              <IconButton
                size="sm"
                variant="plain"
                disabled={!canPrev}
                onClick={handlePrev}
                aria-label="Catégorie précédente"
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  color: canPrev ? ctaAccentColor : withAlpha(colorTokens.text.primary, 0.2),
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: "1.6rem" }} />
              </IconButton>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {groups.map(({ id }, i) => (
                  <Box
                    key={id}
                    onClick={() => setActiveIdx(i)}
                    role="button"
                    aria-label={`Catégorie ${String(i + 1)}`}
                    sx={{
                      width: i === activeIdx ? 24 : 8,
                      height: 8,
                      borderRadius: 999,
                      background:
                        i === activeIdx
                          ? ctaAccentColor
                          : withAlpha(colorTokens.text.primary, 0.25),
                      transition: "width .2s ease, background .2s ease",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </Box>

              <IconButton
                size="sm"
                variant="plain"
                disabled={!canNext}
                onClick={handleNext}
                aria-label="Catégorie suivante"
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  color: canNext ? ctaAccentColor : withAlpha(colorTokens.text.primary, 0.2),
                }}
              >
                <ChevronRightIcon sx={{ fontSize: "1.6rem" }} />
              </IconButton>
            </Box>
          )}

          <MenuFooter />
        </Box>
      )}
    </React.Fragment>
  );
};

export default MenuPage;
